import { computed, reactive } from 'vue';

import type { WSMarginUpdate, WSTradingUpdatePerps, OpenPositionsSnapshot } from '@exchange/libs/account/service/src/lib/wss-account-messages';
import { type BalanceModel, balanceService } from '@exchange/libs/balances/service/src';
import { logger } from '@exchange/libs/utils/simple-logger/src';

import { getAccountRatioStatus, type AccountRatioStatus } from './account-ratio';
import { CURRENCY_ELIGIBLE_FOR_MARGIN } from './constants.config';

const getDefaultState = () => ({
  accountRatio: null,
  accountRatioStatus: null,
  marginUsed: 0,
  maintenanceMargin: 0,
  quoteCurrencyBalance: 0,
});

type MarginState = {
  accountRatio: number | null;
  accountRatioStatus: AccountRatioStatus | null;
  marginUsed: number;
  maintenanceMargin: number;
  quoteCurrencyBalance: number;
};

class MarginService {
  private readonly logPrefix = 'MarginService:';

  private readonly state = reactive<MarginState>(getDefaultState());

  public accountRatio = computed(() => this.state.accountRatio);

  public accountRatioStatus = computed(() => this.state.accountRatioStatus);

  public balance = computed(() => {
    const used = this.state.marginUsed;
    const available = Math.max(this.state.quoteCurrencyBalance - used, 0);

    return {
      available,
      used,
    };
  });

  public equity = computed(() => this.state.quoteCurrencyBalance);

  public maintenanceMargin = computed(() => this.state.maintenanceMargin);

  public marginUsedPercentage = computed(() => {
    const { marginUsed, quoteCurrencyBalance } = this.state;

    if (quoteCurrencyBalance <= 0) {
      return 0;
    }

    return ((marginUsed / quoteCurrencyBalance) * 100).toFixed(2);
  });

  public processMarginUpdate(event: WSMarginUpdate) {
    logger.info(`${this.logPrefix} Processing margin update:`, event);

    const { maintenance_margin: maintenanceMargin, margin_used: marginUsed, ratio, status } = event;
    const stateUpdate: Partial<MarginState> = {
      maintenanceMargin: Number(maintenanceMargin),
      marginUsed: Number(marginUsed),
      accountRatio: ratio,
      accountRatioStatus: status,
    };

    logger.info(`${this.logPrefix} Updating margin state from margin update:`, stateUpdate);

    Object.assign(this.state, stateUpdate);
  }

  public processTradingUpdate(event: WSTradingUpdatePerps) {
    logger.info(`${this.logPrefix} Processing trading update (${event.type}):`, event);

    const { maintenance_margin: maintenanceMargin, margin_used: marginUsed } = event;
    const stateUpdate: Partial<MarginState> = {
      maintenanceMargin: Number(maintenanceMargin),
      marginUsed: Number(marginUsed),
    };

    if ('account_ratio' in event) {
      stateUpdate.accountRatio = Number(event.account_ratio);
      stateUpdate.accountRatioStatus = getAccountRatioStatus(Number(event.account_ratio));
    }

    if ('quote_currency_balance' in event) {
      stateUpdate.quoteCurrencyBalance = Number(event.quote_currency_balance);
    }

    logger.info(`${this.logPrefix} Updating margin state from trading update (${event.type}):`, stateUpdate);

    Object.assign(this.state, stateUpdate);
  }

  public setStateFromOpenPositionsSnapshot(event: OpenPositionsSnapshot) {
    logger.info(`${this.logPrefix} Setting margin state from open positions snapshot:`, event);

    const { account_ratio: accountRatio, maintenance_margin: maintenanceMargin, margin_used: marginUsed } = event;
    const stateUpdate: Partial<MarginState> = {
      maintenanceMargin: Number(maintenanceMargin),
      marginUsed: Number(marginUsed),
    };

    if (accountRatio !== null) {
      stateUpdate.accountRatio = Number(accountRatio);
      stateUpdate.accountRatioStatus = getAccountRatioStatus(Number(accountRatio));
    }

    logger.info(`${this.logPrefix} Updating margin state from open positions snapshot:`, stateUpdate);

    Object.assign(this.state, stateUpdate);
  }

  public updateStateFromBalances(balances?: BalanceModel[], { reset = false }: { reset?: boolean } = {}) {
    logger.info(`${this.logPrefix} Updating margin state from balances:`, balances);

    const stateUpdate: Partial<MarginState> = reset ? getDefaultState() : {};
    const marginCurrencyBalance = balances?.find((b) => b.currencyCode === CURRENCY_ELIGIBLE_FOR_MARGIN) || balanceService.balances.value[CURRENCY_ELIGIBLE_FOR_MARGIN];

    if (marginCurrencyBalance) {
      Object.assign(stateUpdate, {
        marginUsed: marginCurrencyBalance.marginUsed?.toNumber() ?? 0,
        quoteCurrencyBalance: marginCurrencyBalance.available.toNumber(),
      });
    }

    logger.info(`${this.logPrefix} Updating margin state from balances:`, stateUpdate);

    Object.assign(this.state, stateUpdate);
  }

  public reset() {
    logger.info(`${this.logPrefix} Resetting margin state`);

    Object.assign(this.state, getDefaultState());
  }
}

const marginService = new MarginService();

export default marginService;
